import { apiBase } from "@/api/base";
import { startIdleClock } from "@/lib/idle-session";

/*
 * Auth-aware API client.
 *
 * The API issues a short-lived access token and a single-use refresh token. Every call
 * goes through getAccessToken(), which silently refreshes a (nearly) expired access token;
 * concurrent callers share one refresh, because the server treats a second use of the same
 * refresh token as theft and ends every session.
 *
 * Writes to editable records send the loaded `version` as `If-Match` (apiSend). A stale
 * version comes back as HTTP 409 and is thrown as ConflictError with `isStale`.
 */

const ACCESS_KEY = "pbo.access";
const REFRESH_KEY = "pbo.refresh";
const REFRESH_MARGIN_SECONDS = 30;
const RENEW_LOCK = "pbo.renew";
/** How long a sign-out waits for the server to revoke the session. */
const LOGOUT_REVOKE_TIMEOUT_MS = 5000;

/** Dispatched on window when the session ends (logout, refused refresh, 401). */
export const UNAUTHORIZED_EVENT = "pbo:unauthorized";

export type TokenPair = {
  access_token: string;
  refresh_token: string;
  token_type: "bearer";
  expires_in: number;
};

export type Me = {
  id: string;
  username: string;
  full_name: string;
  email: string | null;
  is_active: boolean;
  locked_until: string | null;
  last_login_at: string | null;
  created_at: string;
};

export class AuthError extends Error {}

/** A failed API call. `detail` is FastAPI's parsed `detail` (string, object or 422 list). */
export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly detail: unknown = undefined,
  ) {
    super(message);
  }
}

const STALE_MESSAGE = "This record was changed by someone else. Reload it and try again.";

function isStaleDetail(detail: unknown): detail is { current_version?: number | null } {
  return typeof detail === "object" && detail !== null && "current_version" in detail;
}

function conflictMessage(detail: unknown): string {
  if (typeof detail === "string") return detail;
  if (isStaleDetail(detail)) return STALE_MESSAGE;
  return "This change conflicts with the current data.";
}

/**
 * HTTP 409. `isStale` when another director saved a newer version of the record (If-Match
 * mismatch; `currentVersion` when the API reports it). Otherwise a business conflict whose
 * message comes from the API, e.g. "username already exists".
 */
export class ConflictError extends Error {
  readonly isStale: boolean;
  readonly currentVersion: number | undefined;

  constructor(public readonly detail: unknown) {
    super(conflictMessage(detail));
    this.isStale = isStaleDetail(detail);
    this.currentVersion =
      isStaleDetail(detail) && typeof detail.current_version === "number"
        ? detail.current_version
        : undefined;
  }
}

// localStorage can be unavailable (private mode, tests in node); fall back to memory.
const memory = new Map<string, string>();

function getItem(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return memory.get(key) ?? null;
  }
}

function setItem(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    memory.set(key, value);
  }
}

function removeItem(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch {
    /* storage unavailable */
  }
  memory.delete(key);
}

function storeTokens(pair: Pick<TokenPair, "access_token" | "refresh_token">): void {
  setItem(ACCESS_KEY, pair.access_token);
  setItem(REFRESH_KEY, pair.refresh_token);
}

/** Seconds until the JWT's `exp` (negative when expired), or null if unreadable. */
function secondsUntilExpiry(token: string): number | null {
  try {
    const payload = token.split(".")[1];
    if (!payload) return null;
    const claims = JSON.parse(atob(payload.replace(/-/g, "+").replace(/_/g, "/"))) as {
      exp?: unknown;
    };
    return typeof claims.exp === "number" ? claims.exp - Date.now() / 1000 : null;
  } catch {
    return null;
  }
}

export function isAuthenticated(): boolean {
  return getItem(ACCESS_KEY) !== null || getItem(REFRESH_KEY) !== null;
}

/** Drop both tokens and tell the app to show the login screen. */
export function clearSession(): void {
  removeItem(ACCESS_KEY);
  removeItem(REFRESH_KEY);
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event(UNAUTHORIZED_EVENT));
  }
}

// Every tab of this browser shares the tokens, so a sign-out in one signs the others out.
// Storage is left alone: a tab back from the back/forward cache replays old events while a
// newer session may already be stored, which is why it checks what is stored now.
if (typeof window !== "undefined") {
  window.addEventListener("storage", (event) => {
    const tokens = event.key === null || event.key === ACCESS_KEY || event.key === REFRESH_KEY;
    if (tokens && !isAuthenticated()) window.dispatchEvent(new Event(UNAUTHORIZED_EVENT));
  });
}

function loginErrorMessage(status: number): string {
  switch (status) {
    case 401:
      return "Incorrect username or password.";
    case 423:
      return "Account locked after repeated failed sign-ins. Try again in 15 minutes or ask another director to reset your password.";
    case 429:
      return "Too many attempts. Wait a minute and try again.";
    default:
      return `Sign-in failed (HTTP ${status}).`;
  }
}

export async function login(username: string, password: string): Promise<void> {
  const res = await fetch(`${apiBase()}/token`, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: new URLSearchParams({ username, password }).toString(),
  });
  if (!res.ok) {
    throw new AuthError(loginErrorMessage(res.status));
  }
  storeTokens((await res.json()) as TokenPair);
  startIdleClock();
}

let refreshInFlight: Promise<string> | null = null;

async function refreshAccessToken(): Promise<string> {
  const refreshToken = getItem(REFRESH_KEY);
  if (refreshToken === null) {
    throw new AuthError("Not signed in.");
  }
  const res = await fetch(`${apiBase()}/token/refresh`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ refresh_token: refreshToken }),
  });
  if (!res.ok) {
    clearSession();
    throw new AuthError("Your session has ended. Please sign in again.");
  }
  // Signed out (or in again) while the renewal was on its way: storing it would bring the
  // ended session back, and clearing storage would end the new one.
  if (getItem(REFRESH_KEY) !== refreshToken) {
    throw new AuthError("Your session has ended. Please sign in again.");
  }
  const pair = (await res.json()) as TokenPair;
  storeTokens(pair);
  return pair.access_token;
}

/** A usable access token, refreshing it first when it is missing or about to expire. */
export async function getAccessToken(): Promise<string> {
  const access = getItem(ACCESS_KEY);
  if (access !== null && (secondsUntilExpiry(access) ?? Infinity) > REFRESH_MARGIN_SECONDS) {
    return access;
  }
  if (getItem(REFRESH_KEY) === null) {
    if (access !== null) return access;
    throw new AuthError("Not signed in.");
  }
  if (refreshInFlight === null) {
    refreshInFlight = renewInTurn().finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
}

/**
 * The tabs of this browser share one refresh token, and a second use of it ends every session,
 * so they renew one at a time (Web Locks); a tab that waited uses the token just stored.
 */
async function renewInTurn(): Promise<string> {
  // Older Android WebViews, insecure origins and test DOMs have no Web Locks.
  if (typeof navigator === "undefined" || !("locks" in navigator)) return refreshAccessToken();
  return navigator.locks.request(RENEW_LOCK, () => {
    const access = getItem(ACCESS_KEY);
    const fresh =
      access !== null && (secondsUntilExpiry(access) ?? Infinity) > REFRESH_MARGIN_SECONDS;
    return fresh ? access : refreshAccessToken();
  });
}

/**
 * Sign out here at once, whatever the network does, then revoke the director's refresh tokens
 * on the server (`POST /logout`) with the tokens just dropped. An expired access token is
 * swapped for a bearer with its refresh token; nothing from that swap is stored, and /logout
 * revokes the rotated token too. A refused swap means the session had already ended. Offline
 * or slower than 5 s, the revoke is given up: the tokens are already gone from this device.
 */
export async function logout(): Promise<void> {
  let bearer = getItem(ACCESS_KEY);
  const refreshToken = getItem(REFRESH_KEY);
  clearSession();
  try {
    // Optional call: Android WebViews older than Chrome 103 lack AbortSignal.timeout.
    const signal = AbortSignal.timeout?.(LOGOUT_REVOKE_TIMEOUT_MS);
    if (bearer === null || (secondsUntilExpiry(bearer) ?? Infinity) <= REFRESH_MARGIN_SECONDS) {
      if (refreshToken === null) return;
      const res = await fetch(`${apiBase()}/token/refresh`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refresh_token: refreshToken }),
        signal,
      });
      if (!res.ok) return;
      bearer = ((await res.json()) as TokenPair).access_token;
    }
    await fetch(`${apiBase()}/logout`, {
      method: "POST",
      headers: { Authorization: `Bearer ${bearer}` },
      signal,
    });
  } catch {
    /* offline or slow: the tokens are already dropped here */
  }
}

async function readError(res: Response): Promise<{ message: string; detail: unknown }> {
  const body = (await res.json().catch(() => null)) as { detail?: unknown } | null;
  const detail = body?.detail;
  if (typeof detail === "string") return { message: detail, detail };
  if (res.status === 422) return { message: "Please correct the highlighted fields.", detail };
  return { message: `HTTP ${res.status}`, detail };
}

/** fetch() with the bearer token; throws AuthError (401), ConflictError (409) or ApiError. */
export async function apiFetch(path: string, init: RequestInit = {}): Promise<Response> {
  const token = await getAccessToken();
  const headers = new Headers(init.headers);
  headers.set("Authorization", `Bearer ${token}`);
  const res = await fetch(`${apiBase()}${path}`, { ...init, headers });
  if (res.status === 401) {
    clearSession();
    throw new AuthError("Your session has ended. Please sign in again.");
  }
  if (res.status === 409) {
    const { detail } = await readError(res);
    throw new ConflictError(detail);
  }
  if (!res.ok) {
    const { message, detail } = await readError(res);
    throw new ApiError(res.status, message, detail);
  }
  return res;
}

export async function apiJson<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await apiFetch(path, init);
  return (await res.json()) as T;
}

export type WriteMethod = "POST" | "PUT" | "PATCH" | "DELETE";

/** JSON write. `version` is sent as If-Match (optimistic concurrency). 204 resolves undefined. */
export async function apiSend<T>(
  path: string,
  method: WriteMethod,
  body?: unknown,
  version?: number,
): Promise<T> {
  const headers = new Headers();
  if (body !== undefined) headers.set("Content-Type", "application/json");
  if (version !== undefined) headers.set("If-Match", String(version));
  const res = await apiFetch(path, {
    method,
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

/** Multipart upload; the browser sets the multipart Content-Type (with boundary) itself. */
export async function apiUpload<T>(
  path: string,
  method: "POST" | "PUT",
  form: FormData,
  version?: number,
): Promise<T> {
  const headers = new Headers();
  if (version !== undefined) headers.set("If-Match", String(version));
  const res = await apiFetch(path, { method, headers, body: form });
  return (await res.json()) as T;
}

/**
 * Field messages from a 422 response, keyed by field name (the first string after `body` in
 * `loc`). Model-level messages (loc `["body"]`) go under `_form`. First message per key wins.
 */
export function fieldErrors(error: unknown): Record<string, string> {
  if (!(error instanceof ApiError) || error.status !== 422 || !Array.isArray(error.detail)) {
    return {};
  }
  const errors: Record<string, string> = {};
  for (const item of error.detail as unknown[]) {
    if (typeof item !== "object" || item === null) continue;
    const { loc, msg } = item as { loc?: unknown; msg?: unknown };
    if (typeof msg !== "string") continue;
    const path = Array.isArray(loc) ? (loc as unknown[]).slice(1) : [];
    const field = path.find((part): part is string => typeof part === "string");
    errors[field ?? "_form"] ??= msg.replace(/^Value error, /, "");
  }
  return errors;
}

export function fetchMe(): Promise<Me> {
  return apiJson<Me>("/me");
}
